import type { ListModule } from '../config/constants';
import { forMaps, forSearch, type SearchUrlFields } from './searchUrl';

export const CAPTURE_ENGINE_SNAPSHOT_VERSION = 2;
export const LEGACY_CAPTURE_ENGINE_SNAPSHOT_VERSION = 1;

export interface CaptureScheduleNear {
  location: string;
  name?: string;
  distance?: number;
  population?: number;
  finished?: boolean;
  /** ACK que concluiu a etapa; impede que um retry avance o schedule de novo. */
  ackId?: string;
}

export interface CaptureScheduleRow extends SearchUrlFields {
  /** Índice estável atribuído pelo backend v2; o motor mantém a ordem do array como cursor. */
  i?: number;
  code?: string;
  linkedin?: string;
  location?: string;
  near?: CaptureScheduleNear[];
  finished?: boolean;
  /** ACK que concluiu a etapa principal. */
  ackId?: string;
}

/** `near: -1` identifica a etapa principal; 0+ identifica o segundo nível de places. */
export interface CaptureCursor {
  i: number;
  near: number;
}

export type CapturePhase = 'running' | 'paused';

interface CaptureEngineSnapshotBase {
  listId: string;
  module: ListModule;
  language: string;
  schedule: CaptureScheduleRow[];
  /** Etapa aberta no momento da pausa; ela é reprocessada até receber confirmação. */
  current?: CaptureCursor;
  /** Piso persistido que impede regressão visual após pausa/restauração. */
  progressFloor: number;
  pauseReason?: CapturePauseReason;
}

export interface CaptureEngineSnapshot extends CaptureEngineSnapshotBase {
  version: typeof CAPTURE_ENGINE_SNAPSHOT_VERSION;
  /** Identidade imutável desta execução, preservada em pausa, reload e retomada. */
  runId: string;
  /** Única autoridade durável para responder se esta execução está rodando. */
  phase: CapturePhase;
}

/** Snapshot gravado antes de `phase`/`runId`; aceito somente para migração pelo runtime. */
export interface LegacyCaptureEngineSnapshot extends CaptureEngineSnapshotBase {
  version: typeof LEGACY_CAPTURE_ENGINE_SNAPSHOT_VERSION;
  paused: boolean;
}

export type StoredCaptureEngineSnapshot = CaptureEngineSnapshot | LegacyCaptureEngineSnapshot;

export type CapturePauseReason =
  | 'user_requested'
  | 'email_dns_unavailable'
  | 'quota_exceeded'
  | 'panel_closed'
  | 'search_tab_closed'
  | 'delivery_failed'
  | 'extension_unavailable';

export interface CaptureStep {
  cursor: CaptureCursor;
  row: CaptureScheduleRow;
  near?: CaptureScheduleNear;
  url: string;
  /** Posição global 1-based, contando a linha principal e cada item de `near[]`. */
  position: number;
  total: number;
  completed: number;
  progress: number;
}

export interface CreateCaptureEngineOptions {
  runId: string;
  listId: string;
  module: ListModule;
  schedule: readonly CaptureScheduleRow[];
  language?: string;
  phase?: CapturePhase;
}

export interface CaptureEngineSnapshotMigration {
  snapshot: CaptureEngineSnapshot;
  migrated: boolean;
}

function cloneCursor(cursor: CaptureCursor | undefined): CaptureCursor | undefined {
  return cursor ? { ...cursor } : undefined;
}

function cloneSchedule(schedule: readonly CaptureScheduleRow[]): CaptureScheduleRow[] {
  return schedule.map((row) => ({
    ...row,
    near: row.near?.map((item) => ({ ...item })),
  }));
}

function clampProgress(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

function cursorKey(cursor: CaptureCursor): string {
  return `${cursor.i}:${cursor.near}`;
}

function sameCursor(left: CaptureCursor | undefined, right: CaptureCursor): boolean {
  return left?.i === right.i && left.near === right.near;
}

/**
 * Converte o snapshot v1 sem inventar identidade dentro do motor. O `CaptureRuntime`, único
 * escritor de `captureState`, fornece e persiste o runId uma única vez ao adotar o estado.
 */
export function migrateCaptureEngineSnapshot(
  stored: StoredCaptureEngineSnapshot,
  createRunId: () => string,
): CaptureEngineSnapshotMigration {
  if (stored.version === CAPTURE_ENGINE_SNAPSHOT_VERSION) {
    return { snapshot: stored, migrated: false };
  }

  const { paused, ...legacy } = stored;
  return {
    snapshot: {
      ...legacy,
      version: CAPTURE_ENGINE_SNAPSHOT_VERSION,
      runId: createRunId(),
      phase: paused ? 'paused' : 'running',
    },
    migrated: true,
  };
}

/**
 * Motor serializável do schedule recebido do backend.
 *
 * A posição só é concluída depois do ACK do lote. Enquanto isso, `current` permanece no
 * snapshot; fechar e reabrir o painel faz a mesma etapa ser reenviada, sem pular nem reiniciar.
 * O ACK é idempotente no IndexedDB, então reprocessar a etapa em voo é seguro.
 */
export class CaptureEngine {
  private readonly state: CaptureEngineSnapshot;
  private readonly slots: CaptureCursor[];
  private readonly slotIndexes: Map<string, number>;
  private readonly acknowledgedAckIds: Set<string>;
  private completedCount: number;

  constructor(options: CreateCaptureEngineOptions | CaptureEngineSnapshot) {
    const restoring = 'version' in options;
    this.state = restoring
      ? {
          ...options,
          schedule: cloneSchedule(options.schedule),
          current: cloneCursor(options.current),
          progressFloor: clampProgress(options.progressFloor),
        }
      : {
          version: CAPTURE_ENGINE_SNAPSHOT_VERSION,
          runId: options.runId,
          listId: options.listId,
          module: options.module,
          language: options.language ?? 'EN',
          schedule: cloneSchedule(options.schedule),
          progressFloor: 0,
          phase: options.phase ?? 'paused',
        };

    this.slots = [];
    for (const [i, row] of this.state.schedule.entries()) {
      this.slots.push({ i, near: -1 });
      for (let near = 0; near < (row.near?.length ?? 0); near += 1) {
        this.slots.push({ i, near });
      }
    }
    this.slotIndexes = new Map(this.slots.map((cursor, index) => [cursorKey(cursor), index]));
    this.acknowledgedAckIds = new Set<string>();
    for (const row of this.state.schedule) {
      if (row.ackId) this.acknowledgedAckIds.add(row.ackId);
      for (const near of row.near ?? []) {
        if (near.ackId) this.acknowledgedAckIds.add(near.ackId);
      }
    }
    this.completedCount = this.slots.reduce(
      (total, cursor) => total + (this.isCursorFinished(cursor) ? 1 : 0),
      0,
    );
    this.raiseProgressFloor();

    if (this.state.current && !this.isPendingCursor(this.state.current)) {
      delete this.state.current;
    }
  }

  get listId(): string {
    return this.state.listId;
  }

  get module(): ListModule {
    return this.state.module;
  }

  get runId(): string {
    return this.state.runId;
  }

  get phase(): CapturePhase {
    return this.state.phase;
  }

  get paused(): boolean {
    return this.state.phase === 'paused';
  }

  get total(): number {
    return this.slots.length;
  }

  get completed(): number {
    return this.completedCount;
  }

  get progress(): number {
    if (this.total === 0) return 100;
    return Math.max(this.state.progressFloor, (this.completedCount / this.total) * 100);
  }

  get finished(): boolean {
    return this.completedCount === this.total;
  }

  get pauseReason(): CapturePauseReason | undefined {
    return this.state.pauseReason;
  }

  hasAcknowledged(ackId: string): boolean {
    return this.acknowledgedAckIds.has(ackId);
  }

  pause(reason?: CapturePauseReason): void {
    this.state.phase = 'paused';
    if (reason) this.state.pauseReason = reason;
  }

  resume(): void {
    this.state.phase = 'running';
    delete this.state.pauseReason;
  }

  /**
   * Devolve a etapa corrente até que ela seja confirmada. Em uma restauração, isso mantém
   * exatamente a aba que estava em voo; sem cursor persistido, encontra a primeira pendente.
   */
  next(): CaptureStep | null {
    let cursor = this.state.current;
    if (!cursor || !this.isPendingCursor(cursor)) {
      cursor = this.findPendingFrom(0);
      if (!cursor) {
        delete this.state.current;
        return null;
      }
      this.state.current = cursor;
    }

    return this.toStep(cursor);
  }

  /** Marca uma etapa como concluída; repetir o mesmo ACK é um no-op e não reduz progresso. */
  markCompleted(cursor: CaptureCursor, ackId?: string): boolean {
    if (ackId && this.acknowledgedAckIds.has(ackId)) return false;
    const slotIndex = this.slotIndexes.get(cursorKey(cursor));
    if (slotIndex === undefined || this.isCursorFinished(cursor)) return false;

    const row = this.state.schedule[cursor.i];
    if (!row) return false;
    if (cursor.near < 0) {
      row.finished = true;
      if (ackId) row.ackId = ackId;
    } else {
      const near = row.near?.[cursor.near];
      if (!near) return false;
      near.finished = true;
      if (ackId) near.ackId = ackId;
    }

    if (ackId) this.acknowledgedAckIds.add(ackId);
    this.completedCount += 1;
    this.raiseProgressFloor();

    if (sameCursor(this.state.current, cursor)) {
      const following = this.findPendingFrom(slotIndex + 1) ?? this.findPendingFrom(0);
      if (following) this.state.current = following;
      else delete this.state.current;
    }
    return true;
  }

  /** Etapa aberta (ou a primeira pendente) sem mexer no cursor; null quando tudo terminou. */
  peekCurrent(): CaptureCursor | null {
    const cursor = this.state.current;
    if (cursor && this.isPendingCursor(cursor)) return { ...cursor };
    return this.findPendingFrom(0) ?? null;
  }

  markCurrentCompleted(ackId?: string): boolean {
    return this.state.current ? this.markCompleted(this.state.current, ackId) : false;
  }

  snapshot(): CaptureEngineSnapshot {
    return {
      ...this.state,
      schedule: cloneSchedule(this.state.schedule),
      current: cloneCursor(this.state.current),
      progressFloor: this.progress,
    };
  }

  private isPendingCursor(cursor: CaptureCursor): boolean {
    return this.slotIndexes.has(cursorKey(cursor)) && !this.isCursorFinished(cursor);
  }

  private isCursorFinished(cursor: CaptureCursor): boolean {
    const row = this.state.schedule[cursor.i];
    if (!row) return false;
    return cursor.near < 0 ? row.finished === true : row.near?.[cursor.near]?.finished === true;
  }

  private findPendingFrom(start: number): CaptureCursor | undefined {
    for (let index = start; index < this.slots.length; index += 1) {
      const cursor = this.slots[index];
      if (cursor && !this.isCursorFinished(cursor)) return { ...cursor };
    }
    return undefined;
  }

  private raiseProgressFloor(): void {
    const calculated = this.total === 0 ? 100 : (this.completedCount / this.total) * 100;
    this.state.progressFloor = Math.max(this.state.progressFloor, calculated);
  }

  private toStep(cursor: CaptureCursor): CaptureStep {
    const row = this.state.schedule[cursor.i];
    if (!row) throw new Error('Cursor de captura aponta para uma linha inexistente.');
    const near = cursor.near >= 0 ? row.near?.[cursor.near] : undefined;
    const slotIndex = this.slotIndexes.get(cursorKey(cursor));
    if (slotIndex === undefined) throw new Error('Cursor de captura não pertence ao schedule.');

    let url: string;
    if (this.state.module === 'places') {
      const [baseGeo = '', baseName = ''] = (row.location ?? '').split('|');
      const geo = near?.location ?? baseGeo;
      const name = near?.name ?? baseName;
      url = forMaps(row, geo, name, this.state.language);
    } else {
      url = forSearch(row);
    }

    return {
      cursor: { ...cursor },
      row: { ...row, near: row.near?.map((item) => ({ ...item })) },
      near: near ? { ...near } : undefined,
      url,
      position: slotIndex + 1,
      total: this.total,
      completed: this.completedCount,
      progress: this.progress,
    };
  }
}

export interface BuildPhoneScheduleInput {
  engines: readonly string[];
  sites: readonly string[];
  terms: readonly string[];
  keywords: readonly string[];
  code?: string;
}

const PREPARE_TYPE_1 = [
  'cnpj.services',
  'qive.com.br',
  'casadosdados.com.br',
  'arquivei.com.br',
  'cnpj.linkana.com',
  'consultascnpj.com',
] as const;
const PREPARE_TYPE_2 = [
  'cadastroempresa.com.br',
  'situacaocadastral.info',
  'informecadastral.com.br',
  'diariocidade.com',
  'cnpj.biz',
  'cnpj.info',
  'cnpjrocks.com',
  'empresadois.com.br',
  'nacionalconsultas.com.br',
] as const;
const PREPARE_TYPE_3 = ['consultas.plus', 'inteligen.com.br', 'cnpja.com'] as const;

/** Porte de `prepareKeyword` [api.js:171]. */
export function prepareScheduleKeyword(input = '', site = ''): string {
  const digits = input.replace(/\D/g, '');
  if (digits.length !== 7) return input.trim();

  if (PREPARE_TYPE_1.some((part) => site.includes(part))) return digits;
  if (PREPARE_TYPE_2.some((part) => site.includes(part))) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 4)}-${digits.slice(4, 5)}-${digits.slice(5)}`;
  }
  if (PREPARE_TYPE_3.some((part) => site.includes(part))) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 5)}/${digits.slice(5)}`;
  }
  return input.trim().substring(0, 60);
}

/**
 * Porte do núcleo combinatório de phones [api.js:2719-2741].
 *
 * A origem cria `row` uma vez por termo e empilha a mesma referência para todas as keywords.
 * Aqui o objeto nasce dentro do último loop: uma linha independente por combinação.
 */
export function buildPhoneSchedule(input: BuildPhoneScheduleInput): CaptureScheduleRow[] {
  const schedule: CaptureScheduleRow[] = [];

  for (const engine of input.engines) {
    for (const rawSite of input.sites) {
      const site = ['bing', 'aol'].includes(engine)
        ? rawSite.replace(/inurl:/gi, '')
        : rawSite;
      for (const term of input.terms) {
        for (const keywordOriginal of input.keywords) {
          if (keywordOriginal.length === 0) continue;
          for (const keyword of keywordOriginal.split(/[,;]/)) {
            if (keyword.length <= 1) continue;
            const row: CaptureScheduleRow = {
              engine,
              phone: `(${term})`,
              keywords: prepareScheduleKeyword(keyword, site),
            };
            if (site) row.site = site;
            if (input.code) row.code = input.code;
            schedule.push(row);
          }
        }
      }
    }
  }

  return schedule;
}
